import { useEffect, useRef, useState } from "react";
import { useInView } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { useReducedMotion } from "@/hooks/useReducedMotion";

/**
 * Counts up to `end` when scrolled into view.
 *
 * `animate={false}` is for values where counting reads as a glitch rather than
 * an effect — a founding year, for instance. Numerals follow the active
 * language so Arabic gets Arabic-Indic digits.
 */
export const CountUp = ({
  end,
  suffix = "",
  duration = 1800,
  animate = true,
  className = "",
}) => {
  const ref = useRef(null);
  const { lang } = useLang();
  const reduced = useReducedMotion();
  const shouldAnimate = animate && !reduced;
  const inView = useInView(ref, { once: true, amount: 0.6 });
  const [value, setValue] = useState(shouldAnimate ? 0 : end);

  const format = (n) =>
    new Intl.NumberFormat(lang === "ar" ? "ar-IQ" : "en-US", {
      useGrouping: false,
    }).format(n);

  useEffect(() => {
    if (!shouldAnimate) {
      setValue(end);
      return;
    }
    if (!inView) return;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(Math.round(eased * end));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, end, duration, shouldAnimate]);

  return (
    <span ref={ref} className={className}>
      {format(value)}
      {suffix}
    </span>
  );
};
